import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../../components/common/Logo";
import { authService } from "../../lib/api/auth";
import { useAuthStore } from "../../stores/authStore";
import { useTranslation, Trans } from "react-i18next";

export const CheckEmailPage = () => {
    const { t } = useTranslation("common");
    const navigate = useNavigate();
    const { isAuthenticated, user, setUser, logout } = useAuthStore();


    const [isResending, setIsResending] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const [resendError, setResendError] = useState<string | null>(null);
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    // Check if user is authenticated and needs verification
    useEffect(() => {
        if (!isAuthenticated) {
            navigate('/?modal=login');
            return;
        }

        // Check verification status
        const checkStatus = async () => {
            try {
                const status = await authService.getVerificationStatus();
                if (status.data.emailVerified) {
                    // Already verified, update user in store and redirect to dashboard
                    if (user) {
                        setUser({ ...user, emailVerified: true, emailVerifiedAt: status.data.emailVerifiedAt || undefined });
                    }
                    navigate('/boards');
                }

                // Set cooldown if applicable
                if (status.data.canResendAfter) {
                    const canResendAt = new Date(status.data.canResendAfter).getTime();
                    const now = Date.now();
                    if (canResendAt > now) {
                        setCooldownSeconds(Math.ceil((canResendAt - now) / 1000));
                    }
                }
            } catch (error) {
                console.error('Failed to check verification status:', error);
            }
        };

        checkStatus();
    }, [isAuthenticated, navigate, user, setUser]);

    // Cooldown countdown
    useEffect(() => {
        if (cooldownSeconds <= 0) return;

        const timer = setInterval(() => {
            setCooldownSeconds(s => Math.max(0, s - 1));
        }, 1000);

        return () => clearInterval(timer);
    }, [cooldownSeconds]);

    const handleResend = async () => {
        setIsResending(true);
        setResendError(null);
        setResendSuccess(false);

        try {
            const response = await authService.resendVerification();
            setResendSuccess(true);

            if (response.data.canResendAfter) {
                const canResendAt = new Date(response.data.canResendAfter).getTime();
                setCooldownSeconds(Math.ceil((canResendAt - Date.now()) / 1000));
            } else {
                setCooldownSeconds(60); // Fallback
            }
        } catch (error: any) {
            const message = error.response?.data?.error || error.message || 'Failed to resend email';
            setResendError(message);

            // Extract cooldown time from error if present
            const match = message.match(/wait (\d+) seconds/);
            if (match) {
                setCooldownSeconds(parseInt(match[1], 10));
            } else {
                const minuteMatch = message.match(/try again in (\d+) minutes/);
                if (minuteMatch) {
                    setCooldownSeconds(parseInt(minuteMatch[1], 10) * 60);
                }
            }
        } finally {
            setIsResending(false);
        }
    };

    const handleUseDifferentEmail = async () => {
        await logout();
        navigate('/?modal=register');
    };


    return (
        <div className="min-h-screen bg-background-page flex flex-col">
            {/* Navigation */}
            <nav className="container-custom py-4 px-4 sm:py-6 sm:px-6">
                <div className="flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2.5">
                        <Logo className="w-6 h-6" />
                        <span className="text-lg sm:text-2xl font-medium font-display text-text-primary">
                            Dashbal
                        </span>
                    </Link>
                </div>
            </nav>

            {/* Main Content */}
            <div className="flex-1 flex items-center justify-center px-4 py-12">
                <div className="w-full max-w-md">
                    <div className="bg-white dark:bg-neutral-900 rounded-xl shadow-2xl p-8 text-center border border-neutral-200 dark:border-neutral-800">
                        {/* Email Icon */}
                        <div className="flex justify-center mb-8 h-8">
                            <h1 className="relative text-2xl font-semibold text-text-primary dark:text-neutral-100">
                                <div className="absolute right-full mr-3 flex items-center gap-2 whitespace-nowrap h-full">
                                    <Logo className="w-7 h-7" />
                                    <span className="w-4 h-4 text-neutral-300 dark:text-neutral-600" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_5d2cdb6d.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_5d2cdb6d.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                </div>
                                {t("auth.checkYourEmail", "Check Your Email")}
                            </h1>
                        </div>

                        <p className="text-neutral-600 dark:text-neutral-400 mb-2">
                            We've sent a verification link to:
                        </p>

                        <p className="font-medium text-text-primary mb-6">
                            {user?.email || 'your email address'}
                        </p>

                        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-4 mb-6 text-left">
                            <p className="text-sm text-neutral-600 dark:text-neutral-300">
                                <strong className="text-text-primary">What to do:</strong>
                            </p>
                            <ol className="text-sm text-neutral-600 dark:text-neutral-400 mt-2 space-y-2 list-decimal list-inside">
                                <li>Open your email inbox</li>
                                <li>Click the verification link in the email</li>
                                <li>You'll be automatically redirected to your dashboard</li>
                            </ol>
                        </div>

                        {/* Required Notice */}
                        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 mb-4">
                            <p className="text-sm text-amber-700 dark:text-amber-400 flex items-center gap-2">
                                <span className="w-4 h-4 flex-shrink-0" style={{maskImage: `url(https://cdn.dashbal.com/assets/icon_97bf86c7.svg)`, WebkitMaskImage: `url(https://cdn.dashbal.com/assets/icon_97bf86c7.svg)`, maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat', maskPosition: 'center', WebkitMaskPosition: 'center', maskSize: 'contain', WebkitMaskSize: 'contain', backgroundColor: 'currentColor', display: 'inline-block'}} role='img' />
                                <span>Email verification is required to access Dashbal.</span>
                            </p>
                        </div>

                        {/* Success Message */}
                        {resendSuccess && (
                            <div className="bg-success-50 dark:bg-success-900/30 border border-success-200 dark:border-success-800 rounded-lg p-3 mb-4">
                                <p className="text-sm text-success-700 dark:text-success-400">
                                    {t("auth.verificationEmailSent", "Verification email sent! Check your inbox.")}
                                </p>
                            </div>
                        )}

                        {/* Error Message */}
                        {resendError && (
                            <div className="bg-error-50 dark:bg-error-900/30 border border-error-200 dark:border-error-800 rounded-lg p-3 mb-4">
                                <p className="text-sm text-error-700 dark:text-error-400">
                                    {resendError}
                                </p>
                            </div>
                        )}

                        {/* Resend Button */}
                        <div className="space-y-3">
                            <button
                                onClick={handleResend}
                                disabled={isResending || cooldownSeconds > 0}
                                className={`btn-secondary w-full py-3 ${cooldownSeconds > 0 ? 'opacity-50 cursor-not-allowed' : ''
                                    }`}
                            >
                                {isResending ? (
                                    <span className="flex items-center justify-center">
                                        <div className="spinner mr-2" />
                                        {t("actions.sending", "Sending...")}
                                    </span>
                                ) : cooldownSeconds > 0 ? (
                                    t("auth.resendIn", { seconds: cooldownSeconds, defaultValue: `Resend in ${cooldownSeconds}s` })
                                ) : (
                                    t("auth.resendEmailPrompt", "Didn't receive it? Resend Email")
                                )}
                            </button>
                        </div>

                        {/* Help Text */}
                        <p className="text-xs text-neutral-400 mt-6">
                            <Trans
                                i18nKey="auth.cantFindEmailHelp"
                                ns="common"
                                components={[
                                    <button
                                        key="useDifferentEmail"
                                        type="button"
                                        onClick={handleUseDifferentEmail}
                                        className="text-accent-primary hover:underline focus:outline-none focus:underline"
                                    />,
                                ]}
                            />
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <footer className="border-t border-neutral-200 dark:border-neutral-800 py-6">
                <div className="container-custom text-center">
                    <p className="text-sm text-neutral-500 dark:text-neutral-500">
                        © {new Date().getFullYear()} Dashbal. All rights reserved.
                    </p>
                </div>
            </footer>
        </div>
    );
};

export default CheckEmailPage;
